import S from 's-js';
import { SArray as ArraySignal, lift } from 's-array';
import * as _ from 'underscore';
import { IAjax } from 'lib/Ajax';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { callAll } from 'lib/callAll';
import { debounceSignal } from 'lib/debounceSignal';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';
import Currency from 'lib/Currency';
import { ValidationWatcher } from 'lib/validationWatcher';
import { groupBy, IGrouping } from 'lib/groupBy';
import { CaseInsensitiveContains } from 'lib/diacriticals';

import { downloadDataUri } from 'common/services/downloadData';

import { Entrypoints } from 'core/app/entrypoints';

import { PCCache } from 'data/PCCache';

import { ApprovedCharge } from 'data/models/ApprovedCharge';
import { BillingSystem } from 'data/models/BillingSystem';
import { DispatchEvent } from 'data/models/DispatchEvent';
import { DomainSummary } from 'data/models/DomainSummary';
import { ExportPayloadData } from 'data/models/ExportPayload';
import { ExportPayloadStatus } from 'server/enums/ExportPayload';
import { PendingExportsResponse, PendingExportsResponseOptions } from 'data/models/PendingExportsResponse';
import { PilotJobOrder } from 'data/models/PilotJobOrder';
import { QuickBooksExportRequest, QuickBooksExportRequestOptions } from 'data/models/QuickBooksExportRequest';
import { TotalPriceForCharges } from 'data/models/Charge';

import { LocalizationContext } from 'common/models/LocalizationContext';

import { RowSelector, RowExpander, RowExpansionMode } from 'portcall/gridcomponents/model';

import { PageAction } from './pagestateenums';
import { ExportGroupList, ExportGroupListParams, ExportGroupOptions, FilterData, PRICE_PRECISION } from './exportgrouplist';

const FilterStorageKey = 'portcall.billing.pendingexports.filter';

export interface PendingExportsParams {
    id : number;
    expansionMode : RowExpansionMode;
    filter? : string;
}

export function PendingExports(
    loader : CachedModelLoader<PendingExportsResponse, PendingExportsResponseOptions>,
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : PCCache,
    domain : DomainSummary,
    tzutils : TZUtils,
    localization : LocalizationContext,
    exportGroupListModel : (params : ExportGroupListParams) => ExportGroupList,
    { id, filter: _filter, expansionMode: _expansionMode } : PendingExportsParams
) {
    const { model, loading } = loader({ Id: id }),
        billingSystem = cache.BillingSystems.update1(null, { Id: id }),
        filter = S.data(_filter || localStorage[FilterStorageKey] as string | undefined || ''),
        debouncedFilter = debounceSignal(filter),
        pageAction = S.data(PageAction.View),
        showActionsMenu = S.data(false),
        showConfirmExport = S.data(false),
        expansionMode = S.data(_expansionMode),
        pjoGroupingKey = (pjo : PilotJobOrder) => {
            const vesselId = pjo.EffectiveVessel() ? pjo.EffectiveVessel()!.Id() : null;
            // TH 05/05/2017 -- Group on vessel and customer.
            // We expect a null VesselId to be very rare here, although it's possible in theory.
            // We'll count two null VesselId's as matching (as long as customer matches of course).
            // For Customer, we expect the ApprovePilotJobOrderCharges endpoint to validate that
            // it's populated. So we shouldn't really get null or empty here.
            // If we do, treat null and empty string as the same.
            return `${vesselId || 0},${pjo.QBTracking.Customer() || ''}`;
        },
        // TH 05/05/2017 -- Group events on Customer only. Ditto PJOs on treatment of null and empty string.
        dispatchEventGroupingKey = (de : DispatchEvent) => de.QBTracking.Customer() || '',
        exportablePJOs = model.ExportablePilotJobOrders.filter(pjo => !pjo.Bills().length),
        exportableEvents = model.ExportableDispatchEvents.filter(de => !de.Bills().length),
        pjoGroups = groupBy(exportablePJOs, pjoGroupingKey)
            .map(grouping => PilotJobOrderExportGroup(cache, expansionMode, grouping)),
        dispatchEventGroups = groupBy(exportableEvents, dispatchEventGroupingKey)
            .map(grouping => DispatchEventExportGroup(expansionMode, grouping)),
        exportGroups = (pjoGroups.map(g => g.ExportGroup) as ArraySignal<ExportGroupOptions>)
            .concat(dispatchEventGroups.map(g => g.ExportGroup)),
        filteredExportGroups = exportGroups
            .filter(g => CaseInsensitiveContains(FilterData(g), debouncedFilter())),
        exportGroupList = exportGroupListModel({
            BillingSystem: billingSystem,
            ExportGroups: filteredExportGroups,
            ShowGroupSelectors: () => pageAction() === PageAction.SelectForExport
        }),
        rowSelector = RowSelector(filteredExportGroups, g => g.SelectedForExport),
        rowExpander = RowExpander(expansionMode, exportGroups, filteredExportGroups, g => g.Expanded),
        exportConfirmationPopup = S.on(showConfirmExport, () => showConfirmExport() ? ExportConfirmationPopup(
            ajax,
            entrypoints,
            cache,
            domain,
            tzutils.now(),
            billingSystem,
            pjoGroups(),
            dispatchEventGroups(),
            () => pageAction(PageAction.View),
            () => showConfirmExport(false)
        ) : null);

    // TH 05/05/2017 -- Deselect all groups on change of filter or of page action.
    S.on(callAll(debouncedFilter, pageAction), () => exportGroups().forEach(g => g.SelectedForExport(false)), null, true);

    return {
        Model: model,
        Loading: loading,
        Filter: filter,
        PageAction: pageAction,
        RowExpander: rowExpander,
        ExportGroupList: exportGroupList,
        ShowActionsMenu: showActionsMenu,
        ShowConfirmExport: showConfirmExport,
        ExportConfirmationPopup: exportConfirmationPopup,
        RowSelector: rowSelector,
        Localization: localization,
        MenuAction: (action : () => void) => () => S.freeze(() => {
            showActionsMenu(false);
            action();
        })
    };
}
export type PendingExports = ReturnType<typeof PendingExports>;

function PilotJobOrderExportGroup(
    cache : PCCache,
    expansionMode : () => RowExpansionMode,
    { key, items } : IGrouping<string, PilotJobOrder>
) {
    const re = /^(\d+),/,
        vesselId = +key.match(re)![1],
        customer = key.replace(re, ''),
        vessel = cache.Vessels.index(vesselId),
        sortedItems = items.orderBy(pjo => pjo.Start.Effective());

    return {
        VesselId: vesselId || null,
        PilotJobOrders: sortedItems,
        ExportGroup: {
            // TH 05/05/2017 -- Placeholder InvoiceNumber.
            // Keep in sync w/ PortCall.Services\Billing\InvoiceNumber.cs.
            InvoiceNumber: () => 'PC-Mxxxx',
            VesselName: () => vessel() && vessel()!.Name(),
            QBCustomerId: () => customer,
            InvoiceDate: () => null,
            ExportDate: () => null,
            UniqueKey: () => 'M' + key,
            Charges: lift(sortedItems.reduce(
                (charges, pjo) =>
                    charges.concat(pjo.ApprovalOfCharges() ? pjo.ApprovalOfCharges()!.Charges() : []),
                () => [] as ApprovedCharge[]
            )),
            SelectedForExport: S.data(false),
            Expanded: S.data(S.sample(expansionMode) === RowExpansionMode.AutoExpand)
        }
    };
}
type PilotJobOrderExportGroup = ReturnType<typeof PilotJobOrderExportGroup>;

function DispatchEventExportGroup(
    expansionMode : () => RowExpansionMode,
    { key, items } : IGrouping<string, DispatchEvent>
) {
    const sortedItems = items.orderBy(de => de.Start());

    return {
        DispatchEvents: sortedItems,
        ExportGroup: {
            // TH 05/05/2017 -- Placeholder InvoiceNumber.
            // Keep in sync w/ PortCall.Services\Billing\InvoiceNumber.cs.
            InvoiceNumber: () => 'PC-Exxxx',
            VesselName: () => null,
            QBCustomerId: () => key,
            InvoiceDate: () => null,
            ExportDate: () => null,
            UniqueKey: () => 'E' + key,
            Charges: lift(sortedItems.reduce(
                (charges, de) =>
                    charges.concat(de.ApprovalOfCharges() ? de.ApprovalOfCharges()!.Charges() : []),
                () => [] as ApprovedCharge[]
            )),
            SelectedForExport: S.data(false),
            Expanded: S.data(S.sample(expansionMode) === RowExpansionMode.AutoExpand)
        }
    };
}
type DispatchEventExportGroup = ReturnType<typeof DispatchEventExportGroup>;

export type ExportFinishedStatus = { success: true } | { success: false, errorDetails: string };

function ExportConfirmationPopup(
    ajax : IAjax,
    entrypoints : Entrypoints,
    cache : PCCache,
    domain : DomainSummary,
    now : TZDate,
    billingSystem : BillingSystem,
    pjoGroups : PilotJobOrderExportGroup[],
    dispatchEventGroups : DispatchEventExportGroup[],
    onExportFinished : () => void,
    onCancel : () => void,
) {
    const selectedPJOGroups = pjoGroups.filter(g => g.ExportGroup.SelectedForExport()),
        selectedDispatchEventGroups = dispatchEventGroups.filter(g => g.ExportGroup.SelectedForExport()),
        selectedPJOCharges = selectedPJOGroups.reduce((charges, pjo) =>
            charges.concat(pjo.ExportGroup.Charges()), [] as ApprovedCharge[]),
        selectedEventCharges = selectedDispatchEventGroups.reduce((charges, de) =>
            charges.concat(de.ExportGroup.Charges()), [] as ApprovedCharge[]),
        allCharges = selectedPJOCharges.concat(selectedEventCharges),
        exportTotal = TotalPriceForCharges(lift(() => allCharges), () => billingSystem.Currency() || Currency.USD, PRICE_PRECISION),
        exportRequest = new QuickBooksExportRequest({
            InvoiceDate: now.toJSON()
        }),
        exportClient = ajax.Url<QuickBooksExportRequestOptions, ExportPayloadData>(entrypoints.QuickBooksExport({ Id: billingSystem.Id() })),
        sendingExportRequest = S.data(false),
        validation = ValidationWatcher(exportClient),
        exportFinishedStatus = S.data<ExportFinishedStatus | null>(null);

    return {
        NumberOfInvoices: selectedPJOGroups.length + selectedDispatchEventGroups.length,
        ExportTotal: exportTotal,
        ExportRequest: exportRequest,
        Cancel: onCancel,
        SendExportRequest,
        SendingExportRequest: sendingExportRequest,
        Validation: validation,
        ExportFinishedStatus: exportFinishedStatus,
        SupportHref: () => 'mailto:' + domain.SupportEmail()
    };

    async function SendExportRequest() {
        if (sendingExportRequest()) return;

        sendingExportRequest(true);
        const charges : QuickBooksExportRequestOptions = {
            GroupedPilotJobOrderCharges: selectedPJOGroups.map(g => ({
                VesselId: g.VesselId,
                QBTracking: {
                    Customer: g.ExportGroup.QBCustomerId()
                },
                PilotJobOrders: g.PilotJobOrders().map(pjo => ({
                    Id: pjo.Id(),
                    ApprovalDate: pjo.ApprovalOfCharges() ?
                        pjo.ApprovalOfCharges()!.ApprovalDate().toJSON() :
                        new TZDate(0).toJSON()
                }))
            })),
            GroupedDispatchEventCharges: selectedDispatchEventGroups.map(g => ({
                QBTracking: {
                    Customer: g.ExportGroup.QBCustomerId()
                },
                DispatchEvents: g.DispatchEvents().map(de => ({
                    Id: de.Id(),
                    ApprovalDate: de.ApprovalOfCharges() ?
                        de.ApprovalOfCharges()!.ApprovalDate().toJSON() :
                        new TZDate(0).toJSON()
                }))
            }))
        };

        const req = exportClient.Post(Object.assign(ExportRequestOptions(exportRequest), charges));
        try {
            const payload = await req;
            S.freeze(() => {
                if (payload.Status === ExportPayloadStatus.Formatted) {
                    const dataUri = `data:text/plain;charset=utf-8,${encodeURIComponent(payload.RawExport!)}`;
                    downloadDataUri(dataUri, 'invoices.iif');
                    exportFinishedStatus({ success: true });
                } else {
                    // TH 05/09/2017 -- This represents a case where Bills were successfully
                    // generated for the requested export groups but there was an error
                    // generating the IIF file for those Bills.
                    // This most likely indicates bad seed data (missing item codes, mismatched
                    // currencies).
                    // We'll treat this like a partial success -- we did in fact generate the
                    // Bills for these items, so they'll show up on the PastExports page and
                    // we'll remove them from PendingExports.
                    // But instead of downloading the IIF file (which doesn't exist), we'll
                    // give users a popup saying that something unexpected happened and they
                    // should check PastExports and call support.
                    // This really shouldn't happen as long as our seed data is consistent.
                    // So I don't think our handling has to be perfect here.
                    exportFinishedStatus({
                        success: false,
                        errorDetails: payload.ErrorMessage || 'Unexpected export error.'
                    });
                }

                // TH 05/05/2017 -- Add any bills created to the cache.
                // We won't cache the ExportPayload itself.
                // I figure the IIF itself could get fairly large, and I'm not currently aware of
                // any reason the client needs to hold onto it.
                cache.BillExportPayloadAssignments.update(req.Date, payload.Bills!, []);
                onExportFinished();
            });
        } finally {
            sendingExportRequest(false);
        }
    }
}
export type ExportConfirmationPopup = ReturnType<typeof ExportConfirmationPopup>;

function ExportRequestOptions(req : QuickBooksExportRequest) : QuickBooksExportRequestOptions {
    return JSON.parse(JSON.stringify(req));
}



// WEBPACK FOOTER //
// ./apps/portcall/quickbooksexports/pendingexports.ts